<script lang="ts">
  import type { ProgressStepContext } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import LoadingWrapper from "@kontextwerk/web-sdk/ui/layout/LoadingWrapper.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import WorkflowActionBox from "../workflow-action-box"
  import WorkflowStepFrame from "../workflow-step-frame"
  import type {
    DemoResetProfileDraft,
    DemoResetProfileOutput,
  } from "../../_logic/demo-data-reset-contract"
  import DemoAccessProfileEditor from "./_modules/demo-access-profile-editor"

  const {
    ctx,
    title,
    description,
    helpModalTextId,
    actionBoxLabel,
    roleDescription,
    initial,
    loading = false,
    loadError = "",
    onRetry = null,
    output = null,
    otherAccessEmail = null,
    onOutput,
    onEdit,
  }: {
    ctx: ProgressStepContext
    title: string
    description: string
    helpModalTextId: string
    actionBoxLabel: string
    roleDescription: string
    initial: DemoResetProfileDraft | null
    loading?: boolean
    loadError?: string
    onRetry?: (() => void) | null
    output?: DemoResetProfileOutput | null
    otherAccessEmail?: string | null
    onOutput: (output: DemoResetProfileOutput) => void
    onEdit: () => void
  } = $props()
</script>

<WorkflowStepFrame>
  <SectionTitle
    {title}
    {description}
    {helpModalTextId}
    helpModalTooltip="Hilfe zu diesem Schritt öffnen"
    small={true}
    darkMode={true}
    styleOuter="margin: 0;"
  />
  <WorkflowActionBox label={actionBoxLabel}>
    <p>{roleDescription}</p>
    {#if loading}
      <div class="profile-load-state" role="status" aria-live="polite" aria-busy="true">
        <LoadingWrapper
          loading={true}
          darkMode={true}
          progressLabel="Bestehende Zugänge und Kontaktdaten werden geladen…"
        />
      </div>
    {:else if loadError || !initial}
      <p class="profile-load-error" role="alert">
        {loadError || "Die benötigten Ausgangsdaten fehlen."}
      </p>
    {:else if ctx.readOnly && output}
      <dl class="profile-summary">
        <div>
          <dt>Zugangsmail</dt>
          <dd>{output.accessEmail}</dd>
        </div>
        <div>
          <dt>Passwort</dt>
          <dd>
            {output.passwordMode === "reuse"
              ? "Bestehendes Passwort wird übernommen"
              : "Passwort wird neu gesetzt"}
          </dd>
        </div>
        <div>
          <dt>Kontaktmail</dt>
          <dd>{output.contactEmail}</dd>
        </div>
        <div>
          <dt>Kontaktmobil</dt>
          <dd>{output.contactMobile}</dd>
        </div>
      </dl>
    {:else}
      <DemoAccessProfileEditor {initial} {otherAccessEmail} {onOutput} />
    {/if}
    {#snippet actions()}
      {#if !loading && (loadError || !initial) && onRetry}
        <CTA type="secondaryButton" content="Erneut versuchen" onClick={onRetry} />
      {:else if ctx.readOnly && output}
        <CTA type="secondaryButton" content="Angaben ändern" onClick={onEdit} />
      {/if}
    {/snippet}
  </WorkflowActionBox>
</WorkflowStepFrame>

<style lang="less">
  .profile-load-state {
    min-height: 12.5rem;
  }

  .profile-load-error {
    min-height: 8rem;
    color: var(--danger-100, var(--error-100, var(--text-100)));
  }

  .profile-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    gap: 0.9rem;
    margin: 0;
  }

  .profile-summary div {
    border-left: 0.2rem solid var(--success-100);
    padding-left: 0.65rem;
  }

  .profile-summary dt,
  .profile-summary dd {
    margin: 0;
  }

  .profile-summary dt {
    font-size: 0.72rem;
    font-weight: 900;
    text-transform: uppercase;
  }

  .profile-summary dd {
    overflow-wrap: anywhere;
    margin-top: 0.2rem;
  }
</style>
